import { useCallback, useEffect, useRef, useState } from "react"
import { Mic, MicOff, X, Square } from "lucide-react"
import { Textarea } from "@/components/ui/textarea"
import { describeMicrophoneError, requestMicrophone } from "@/lib/microphone"
import { useMicPermission } from "@/hooks/use-mic-permission"
import { useTranscription } from "@/hooks/use-transcription"
import { TranscriptReviewPanel } from "./TranscriptReviewPanel"

const MAX_SECONDS = 120
const COUNTDOWN_START_SECONDS = 110

// Preferred MIME types in order. Chrome/Firefox pick webm/opus; Safari picks mp4.
const PREFERRED_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg",
]

function pickMimeType() {
  if (typeof MediaRecorder === "undefined") return ""
  for (const type of PREFERRED_MIME_TYPES) {
    try {
      if (MediaRecorder.isTypeSupported(type)) return type
    } catch {
      // Some browsers throw on isTypeSupported for unknown types
    }
  }
  return ""
}

function formatTime(ms) {
  const safe = Number.isFinite(ms) && ms >= 0 ? ms : 0
  const secs = Math.floor(safe / 1000)
  const m = Math.floor(secs / 60)
  const s = secs % 60
  return `${String(m).padStart(1, "0")}:${String(s).padStart(2, "0")}`
}

// Textarea + in-field mic + transcript review for intake step fields.
//
// Previously this file delegated to `useAudioRecorder`, but that hook's
// surface was a different contract — no `elapsedMs`, no `result` blob,
// no `cancel()` — so the timer read "NaN:NaN" and Cancel/Stop were
// non-ops. This version handles getUserMedia + MediaRecorder inline so
// the UX controls (timer, cancel, stop) are backed by real state, and
// delegates transcription upload to `useTranscription` (→ /app/transcriptions),
// which is the endpoint guests are allowed to hit during intake.
export function VoiceField({
  value,
  onChange,
  placeholder,
  rows = 3,
  id,
  ariaLabel,
}) {
  const permission = useMicPermission()
  const transcription = useTranscription()

  const [stage, setStage] = useState("idle") // 'idle' | 'recording' | 'reviewing'
  const [elapsedMs, setElapsedMs] = useState(0)
  const [recorderError, setRecorderError] = useState(null)

  const recorderRef = useRef(null)
  const streamRef = useRef(null)
  const chunksRef = useRef([])
  const mimeTypeRef = useRef("")
  const startedAtRef = useRef(0)
  const tickerRef = useRef(null)
  const autoStopRef = useRef(null)
  const isMountedRef = useRef(true)
  // Cancel is a "throw away the blob" action; it short-circuits the
  // onstop handler via this ref so a cancelled recording never uploads.
  const cancelledRef = useRef(false)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      cleanupTracks()
      stopTicker()
      stopAutoTimer()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const stopTicker = () => {
    if (tickerRef.current) {
      clearInterval(tickerRef.current)
      tickerRef.current = null
    }
  }

  const stopAutoTimer = () => {
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current)
      autoStopRef.current = null
    }
  }

  const cleanupTracks = () => {
    // Null the ref BEFORE calling getTracks so a throw can't leave a
    // dangling reference keeping the mic LED on.
    const stream = streamRef.current
    streamRef.current = null
    if (stream) {
      try {
        stream.getTracks().forEach((t) => t.stop())
      } catch {
        // ignore
      }
    }
    if (recorderRef.current) {
      try {
        if (recorderRef.current.state !== "inactive") recorderRef.current.stop()
      } catch {
        // ignore
      }
      recorderRef.current = null
    }
    chunksRef.current = []
  }

  const handleBlob = useCallback(
    async (blob) => {
      if (!isMountedRef.current) return
      if (blob.size < 512) {
        setRecorderError("That recording was too short. Please try again.")
        setStage("idle")
        return
      }
      setStage("reviewing")
      await transcription.transcribe({
        blob,
        mimeType: mimeTypeRef.current || "audio/webm",
        durationMs: elapsedMs,
      })
    },
    // `elapsedMs` is the freshest duration at upload time; read it inline.
    // `transcription` is stable across renders (it's the whole hook object,
    // but its fields are refs/callbacks so new objects don't matter here).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const startRecording = useCallback(async () => {
    // Double-tap guard: if the previous tap's getUserMedia is still in
    // flight (recorderRef is nulled only after success) OR the state
    // isn't idle, bail. Without this, two rapid mic clicks each run a
    // parallel getUserMedia; one stream ends up orphaned with the mic
    // LED stuck on.
    if (recorderRef.current || stage !== "idle") return

    setRecorderError(null)
    transcription.reset()
    cancelledRef.current = false
    chunksRef.current = []

    let stream
    try {
      // Shared with useAudioRecorder (see @/lib/microphone). This used to be a
      // bare getUserMedia with its own copy of the error wording, which meant
      // an unanswered permission prompt hung the field forever — the promise
      // never settles, so `stage` stayed "idle" with no error and no feedback.
      stream = await requestMicrophone()
    } catch (err) {
      setRecorderError(describeMicrophoneError(err, "Could not access your microphone."))
      return
    }

    if (!isMountedRef.current) {
      stream.getTracks().forEach((t) => t.stop())
      return
    }

    const mime = pickMimeType()
    mimeTypeRef.current = mime || "audio/webm"

    let recorder
    try {
      recorder = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream)
    } catch (err) {
      stream.getTracks().forEach((t) => t.stop())
      setRecorderError(err?.message || "Couldn't start the recorder.")
      return
    }

    streamRef.current = stream
    recorderRef.current = recorder

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data)
    }

    recorder.onerror = (e) => {
      cleanupTracks()
      stopTicker()
      stopAutoTimer()
      setRecorderError(e?.error?.message || "Recording failed.")
      setStage("idle")
    }

    recorder.onstop = () => {
      const chunks = chunksRef.current
      const mimeType = mimeTypeRef.current || "audio/webm"
      chunksRef.current = []
      recorderRef.current = null
      cleanupTracks()
      stopTicker()
      stopAutoTimer()

      if (!isMountedRef.current) return
      if (cancelledRef.current) {
        // User hit Cancel — drop the blob, reset UI to idle.
        cancelledRef.current = false
        setStage("idle")
        setElapsedMs(0)
        return
      }

      const blob = new Blob(chunks, { type: mimeType })
      handleBlob(blob)
    }

    startedAtRef.current = Date.now()
    setElapsedMs(0)
    setStage("recording")
    recorder.start()

    // 1s ticker drives the visible timer.
    tickerRef.current = setInterval(() => {
      if (!isMountedRef.current) return
      setElapsedMs(Date.now() - startedAtRef.current)
    }, 250)

    // Hard cap.
    autoStopRef.current = setTimeout(() => {
      try {
        if (recorderRef.current && recorderRef.current.state === "recording") {
          recorderRef.current.stop()
        }
      } catch {
        // ignore
      }
    }, MAX_SECONDS * 1000)
  }, [handleBlob, transcription])

  const cancelRecording = () => {
    cancelledRef.current = true
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop() // triggers onstop → resets state
      } else {
        // Recorder already inactive — reset UI directly.
        setStage("idle")
        setElapsedMs(0)
      }
    } catch {
      setStage("idle")
      setElapsedMs(0)
    }
    transcription.reset()
  }

  const stopRecording = () => {
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop()
      }
    } catch {
      // ignore
    }
  }

  const handleInsert = (transcript) => {
    const next = value?.trim() ? `${value.trim()}\n${transcript}` : transcript
    onChange(next)
    transcription.reset()
    setElapsedMs(0)
    setStage("idle")
  }

  const handleRedo = () => {
    transcription.reset()
    setElapsedMs(0)
    setStage("idle")
  }

  const elapsedSec = Math.floor(elapsedMs / 1000)
  const secondsLeft = MAX_SECONDS - elapsedSec
  const countdownActive =
    stage === "recording" && elapsedSec >= COUNTDOWN_START_SECONDS

  const micDisabled = permission === "denied" || permission === "unsupported"

  return (
    <div>
      <div className="relative">
        <Textarea
          id={id}
          aria-label={ariaLabel}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          className="pr-12"
          disabled={
            stage === "recording" || transcription.state === "uploading"
          }
        />

        {!micDisabled && stage === "idle" && transcription.state !== "uploading" && (
          <button
            type="button"
            onClick={startRecording}
            aria-label="Record voice input"
            title="Tap to speak"
            className="absolute top-2 right-2 p-2 rounded-full bg-white border border-zinc-200 text-zinc-500 hover:text-kf-red hover:border-kf-red transition-colors"
          >
            <Mic className="w-4 h-4" />
          </button>
        )}

        {micDisabled && (
          <div className="absolute top-2 right-2 p-2 text-zinc-300">
            <MicOff className="w-4 h-4" />
          </div>
        )}
      </div>

      {stage === "recording" && (
        <div
          role="status"
          aria-live="polite"
          className="mt-2 flex items-center gap-3 rounded-md bg-kf-red/10 border border-kf-red/30 px-3 py-2"
        >
          <span className="w-2.5 h-2.5 rounded-full bg-kf-red animate-pulse" />
          <span
            className={`text-sm font-mono ${countdownActive ? "text-kf-red font-bold" : "text-zinc-700"}`}
          >
            {formatTime(elapsedMs)}
          </span>
          {countdownActive && (
            <span className="text-xs text-kf-red">
              Recording stops in {secondsLeft}s
            </span>
          )}
          <div className="ml-auto flex gap-2">
            <button
              type="button"
              onClick={cancelRecording}
              aria-label="Cancel recording"
              className="flex items-center gap-1 px-2 py-1 rounded text-xs text-zinc-600 hover:bg-white"
            >
              <X className="w-3 h-3" />
              Cancel
            </button>
            <button
              type="button"
              onClick={stopRecording}
              aria-label="Stop and transcribe"
              className="flex items-center gap-1 px-2 py-1 rounded bg-kf-red text-white text-xs"
            >
              <Square className="w-3 h-3" />
              Stop
            </button>
          </div>
        </div>
      )}

      {transcription.state === "uploading" && (
        <p
          role="status"
          aria-live="polite"
          className="mt-2 text-sm text-zinc-500"
        >
          Transcribing…
        </p>
      )}

      {transcription.state === "error" && (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {transcription.error}{" "}
          <button
            type="button"
            onClick={handleRedo}
            className="underline ml-1"
          >
            Retry
          </button>
        </p>
      )}

      {recorderError && stage === "idle" && (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {recorderError}
        </p>
      )}

      {stage === "reviewing" && transcription.state === "done" && (
        <TranscriptReviewPanel
          initialText={transcription.text}
          onInsert={handleInsert}
          onRedo={handleRedo}
        />
      )}

      {permission === "denied" && (
        <p className="text-xs text-zinc-500 mt-1">
          Mic access is blocked. Enable microphone for this site in your
          browser settings, then try again.
        </p>
      )}
    </div>
  )
}
